import { Box, Modal, Typography } from '@mui/material';
import _ from 'lodash';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Notistack } from '@/app/shared/components/Notistack';
import { EntityType, SOURCE_CDX } from '@/app/shared/constants/globalConstants';
import { PAGE_TYPE } from '@/app/shared/constants/segmentConstants';
import { useAnalytics } from '@/app/shared/hooks/useAnalytics';
import useEffectOnce from '@/app/shared/hooks/useEffectOnce';
import { useQueryParams, type TParams } from '@/app/shared/hooks/useQueryParams';
import { Loader } from '@/app/shared/ui';
import { formatNavigateURL } from '@/app/shared/utils/uploadStips';
import type { RootState } from '@/store';
import { useAddEventLogMutation } from '@/store/api/eventsApi';
import { useAppSelector } from '@/store/hooks';
import { setStipsId } from '@/store/reducers/authSlice';
import { setCustomErrorMessage, setEncounteredError } from '@/store/reducers/uiSlice';
import {
  selectReferences,
  selectShowErrorModal,
  setFailedFileNames,
  setFileMd5,
  setFilesOcrDetails,
  toggleErrorModal,
} from '@/store/reducers/uploadSlice';
import { useCreateReferenceMutation } from '../uploadStips/components/References/references.services';
import DocumentsForm from './DocumentsForm';
import {
  useFetchUserDocumentsQuery,
  useFetchStipsTypesQuery,
  useFetchAssetsQuery,
  useSendFilesMutation,
  useCdxRedirectMutation,
} from './uploadStips.services';

import type { AdditionalItemData, FilesData } from './types';
import type { SendFilesResponse } from './uploadStips.services';

const fileExt = function (file: string) {
  return file.substring(file.lastIndexOf('.'), file.length);
};

const UploadStips = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [addEventLog] = useAddEventLogMutation();
  const { trackPage, trackClick } = useAnalytics({page_type: PAGE_TYPE.ILENDING_HUB_UPLOAD});
  const queryParams = useQueryParams<TParams>(['authId', 'assetId', 'leadId', 'source']);
  const contactId = useAppSelector((state) => state.auth.contactId) || '';
  const { isAuthenticated: isAuth, isLoading, stipsId } = useSelector((state: RootState) => state.auth);
  const guid = stipsId ?? '';
  const authId = searchParams.get('authId') ?? '';
  const source = searchParams.get('source') ?? '';

  const [failedSubmissionCount, setFailedSubmissionCount] = useState(0);
  const { fileMd5Map, filesOcrDetails } = useAppSelector((state) => state.upload);

  const references = useAppSelector(selectReferences);
  const showErrorModal = useSelector(selectShowErrorModal);
  const [createReference] = useCreateReferenceMutation();

  const {
    data: documents,
    isLoading: isFetchDocumentLoading,
    refetch: refetchDocuments,
  } = useFetchUserDocumentsQuery({ contactId }, { skip: !contactId });

  const { data: stipsTypes = [] } = useFetchStipsTypesQuery();

  const { data: assets } = useFetchAssetsQuery(contactId, {
    skip: !contactId,
  });

  const [sendFiles, { isLoading: isSendFilesLoading }] = useSendFilesMutation();
  const [cdxRedirect] = useCdxRedirectMutation();

  useEffect(() => {
    const stipRequestId =
      documents?.stipsRequestItems?.[0]?.stipRequestId;
  
    if (stipRequestId) {
      dispatch(setStipsId(stipRequestId));
    }
  }, [documents, dispatch]);

  useEffect(() => {
    addEventLog({ event: 'PageReloaded' });

    const handleBeforeUnload = () => {
      navigator.sendBeacon('/api/track-exit', JSON.stringify({ event: 'CustomerExistsThePage' }));
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffectOnce(() => trackPage(PAGE_TYPE.ILENDING_HUB_UPLOAD));

  const getBase64 = useCallback((file: File) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  }, []);

  useEffect(() => {
    if (failedSubmissionCount >= 3 && guid && contactId && source !== SOURCE_CDX) {
      setCustomErrorMessage('Your files have been received but we are unable to read the documents. We will reach out to you.');
      setTimeout(() => {
        navigate(formatNavigateURL(contactId));
      }, 100);
    }
  }, [failedSubmissionCount, guid, contactId, source, dispatch, navigate]);

  const uploadDocuments = useCallback(
    async (docs: any[], isSubmitDisabled: boolean,  togglesWithUploads: string[]): Promise<void> => {
      const referencesPayload = Object.values(references).filter((ref) => Object.keys(ref).length > 0);
      try {
        trackClick('Save All Submitted', {
          total_documents_uploaded: docs.length,
          total_contacts_added: referencesPayload.length,
          toggles_with_uploads: togglesWithUploads,
        });
        let fileResponse: SendFilesResponse | undefined;

        if (!_.isEmpty(docs)) {
          fileResponse = await sendFiles({
            userContactId: contactId,
            docs,
            guid,
          }).unwrap();
        }

        if (fileResponse?.failedFileNames?.length) {
          dispatch(setFailedFileNames(fileResponse.failedFileNames));
          dispatch(setEncounteredError(true));
        } else {
          dispatch(setFailedFileNames([]));
          dispatch(setEncounteredError(false));
        }

        if (fileResponse?.filesOcrDetails) {
          dispatch(setFilesOcrDetails(fileResponse.filesOcrDetails));
        }

        const ocrFailed = fileResponse?.ocrFailed;
        //create references
        try {
          if (referencesPayload.length > 0 && !isSubmitDisabled) {
            await createReference({
              contactId,
              referenceData: { contactReferences: referencesPayload },
            }).unwrap();
          }
        } catch (error) {
          console.error('Failed to submit references:', error);
          Notistack.generalError('Failed to submit references');
        }

        if (authId && source === SOURCE_CDX && !ocrFailed) {
          await cdxRedirect({ contactId, authId }).unwrap();
          window.onbeforeunload = null;
          window.close();
        }

        if (!ocrFailed && source !== SOURCE_CDX) {
          const redirectURL = formatNavigateURL(contactId, queryParams);
          navigate(redirectURL);
        }

        if (ocrFailed) {
          setFailedSubmissionCount((prev) => prev + 1);
          refetchDocuments();
          return;
        }

        setCustomErrorMessage(null);
        setFailedSubmissionCount(0);
      } catch (err) {
        console.error(err);
        Notistack.generalError('Failed to upload documents');
        setFailedSubmissionCount((prev) => prev + 1);
      }
    },
    [
      contactId,
      guid,
      authId,
      source,
      references,
      dispatch,
      sendFiles,
      cdxRedirect,
      refetchDocuments,
      createReference,
      formatNavigateURL,
      navigate,
      queryParams,
    ]
  );

  const createStipsData = useCallback(
    async (
      contactData: FilesData,
      assetData: FilesData,
      additionalData: Record<string, AdditionalItemData>,
      additionalItemsFiles: FilesData,
      togglesWithUploads: string[],
      isSubmitDisabled?: boolean
    ): Promise<void> => {
      if (!documents?.stipsRequestItems?.length || !assets) return;

      const docs: any[] = [];
      const promises: Promise<void>[] = [];

      // process additional items
      for (const doc in additionalData) {
        if (additionalItemsFiles[doc]?.file) {
          const item = additionalItemsFiles[doc].file;
          const determinedStipRequestId = documents?.stipsRequestItems[0].stipRequestId ?? null;

          let determinedEntityId: string | undefined;
          let determinedEntityTypeName: string | undefined;

          const entityTypeId = additionalData[doc].stipData?.entityTypeId;

          if (entityTypeId === EntityType.CONTACT) determinedEntityTypeName = 'Contact';
          else if (entityTypeId === EntityType.ASSET) determinedEntityTypeName = 'Asset';
          else if (entityTypeId === EntityType.DEAL) determinedEntityTypeName = 'Deal';
          else continue;

          if (additionalData[doc].assetData === null) {
            if (determinedEntityTypeName === 'Contact') determinedEntityId = assets.contacts?.[0]?.entityId;
            else if (determinedEntityTypeName === 'Asset') determinedEntityId = assets.assets?.[0]?.entityId;
            else if (determinedEntityTypeName === 'Deal') determinedEntityId = assets.deals?.[0]?.entityId;
          } else determinedEntityId = additionalData[doc].assetData?.entityId;

          promises.push(
            getBase64(item).then((res) => {
              docs.push({
                entityType: entityTypeId,
                entityTypeName: determinedEntityTypeName,
                entityId: determinedEntityId,
                isEntityCoContact: additionalData[doc].coContact,
                stipRequestItemId: null,
                stipRequestId: determinedStipRequestId,
                stipReceivedDate: null,
                stipTypeId: additionalData[doc].stipData?.stipTypeId,
                stipTypeName: additionalData[doc].stipData?.stipTypeName,
                file: {
                  fileContentBase64: res,
                  fileNameWithoutExtension: additionalData[doc].stipData?.stipTypeName,
                  fileExtension: fileExt(item.name),
                },
              });
            })
          );
        }
      }

      // process main documents
      if (documents?.stipsRequestItems?.length) {
        documents?.stipsRequestItems?.forEach((document: any) => {
          const matchData =
            contactData['document_file' + document.stipRequestItemId] || assetData['document_file' + document.stipRequestItemId];

          if (matchData?.file) {
            const item = matchData.file;
            promises.push(
              getBase64(item).then((res) => {
                docs.push({
                  entityType: document.entityType,
                  entityTypeName: document.entityTypeName,
                  entityId: document.entityId,
                  isEntityCoContact: document.isEntityCoContact,
                  stipRequestId: document.stipRequestId,
                  stipRequestItemId: document.stipRequestItemId,
                  stipReceivedDate: document.stipReceivedDate,
                  stipTypeId: document.stipTypeId,
                  stipTypeName: document.stipTypeName,
                  file: {
                    fileContentBase64: res,
                    fileNameWithoutExtension: document.stipTypeName,
                    fileExtension: fileExt(item.name),
                  },
                });
              })
            );
          }
        });
      }

      await Promise.all(promises);

      await uploadDocuments(docs, !!isSubmitDisabled, togglesWithUploads);
    },
    [documents, assets, getBase64, uploadDocuments]
  );

  const handleSetFileMd5 = (stipRequestItemId: string, md5: string) => {
    dispatch(setFileMd5({ stipRequestItemId, md5 }));
  };

  if ((!contactId && !guid && !authId && !source) || isLoading || isFetchDocumentLoading || isSendFilesLoading) {
    return (
      <div className="home__loading">
        <Loader />
      </div>
    );
  }

  if (authId || isAuth) {
    if (!documents?.stipsRequestItems?.length && !isLoading && !isFetchDocumentLoading) {
      return (
        <div className="documents">
          <div className="documents__document-info">
            <Typography
              variant="h3"
              mt={4}
            >
              No Documents found
            </Typography>
          </div>
        </div>
      );
    }
  }

  if (documents) {
    const contactDocuments: any[] = [];
    const coContactDocuments: any[] = [];
    const assetDocuments: any[] = [];

    documents?.stipsRequestItems?.forEach((document: any) => {
      if (document.entityType === EntityType.CONTACT && !document.relatedAssetId && !document.isEntityCoContact) {
        contactDocuments.push(document);
      } else if (document.entityType === EntityType.CONTACT && document.isEntityCoContact) {
        coContactDocuments.push(document);
      } else if (
        document.entityType === EntityType.ASSET ||
        (document.entityType === EntityType.DEAL && document.relatedAssetId)
      ) {
        assetDocuments.push(document);
      }
    });

    return (
      <>
        <div className="documents">
          <Box>
            <Typography fontWeight={700} fontSize={{ xs: 20, sm: 24 }} color="#1a1a1a" mb={1}>
              Upload Documents
            </Typography>
            <Typography fontSize={14} color="#1a1a1a" lineHeight={1.7} mb={3}>
              Uploading your documents helps us verify your information and keeps your refinance moving forward quickly, it only takes a few minutes.
              Simply choose the document to upload, click on the upload icon and choose your file.
            </Typography>
          </Box>
          <div className="documents__document-info">
            <DocumentsForm
              contactDocuments={contactDocuments}
              coContactDocuments={coContactDocuments}
              assetDocuments={assetDocuments}
              stipRequestItems={documents?.stipsRequestItems}
              createStipsData={createStipsData}
              stipsTypes={stipsTypes}
              assets={assets}
              filesOcrDetails={filesOcrDetails}
              fileMd5Map={fileMd5Map}
              setFileMd5={handleSetFileMd5}
              isCDX={!!authId && source === SOURCE_CDX}
            />
          </div>
        </div>

        <Modal open={showErrorModal}>
          <div className="documentsErrorModal__content">
            <div className="documentsErrorModal__title">Error</div>
            <div className="documentsErrorModal__description">Something went wrong. Please try again later.</div>
            <div
              className="documentsErrorModal__ok-button"
              onClick={() => dispatch(toggleErrorModal(false))}
            >
              OK
            </div>
          </div>
        </Modal>
      </>
    );
  }
  return null;
};

export default UploadStips;
